<template>
  <v-container>
    <v-row class="mb-4 align-center">
      <v-col cols="7" sm="4" md="2" class="d-flex align-center">
        <SelectYear v-model="selectedYear" class="year-select flex-grow-1" />
      </v-col>
      <v-col cols="5" sm="auto" class="d-flex align-center justify-end justify-sm-start">
        <v-btn
          color="primary"
          variant="tonal"
          :loading="exporting"
          :disabled="!ranking.clubs.length"
          prepend-icon="mdi-download"
          @click="downloadExport"
        >
          {{ $t('ranking_clubs.export_pdf') }}
        </v-btn>
      </v-col>
      <v-spacer class="d-none d-sm-flex" />
      <v-col cols="12" sm="auto" class="d-flex justify-end">
        <v-btn-toggle
          v-model="viewMode"
          mandatory
          density="comfortable"
          color="primary"
          divided
        >
          <v-btn value="races">{{ $t('ranking_clubs.by_races') }}</v-btn>
          <v-btn value="categories">{{ $t('ranking_clubs.by_categories') }}</v-btn>
        </v-btn-toggle>
      </v-col>
    </v-row>

    <v-container v-if="loading" class="d-flex justify-center align-center fill-height">
      <v-progress-circular indeterminate color="primary" size="50" width="8" />
    </v-container>

    <template v-else>
      <v-alert v-if="!ranking.races.length" type="info" class="mt-4">
        {{ $t('ranking_clubs.no_races') }}
      </v-alert>

      <template v-else>
      <v-card
        v-if="ranking.clubs.length && globalRaces.length"
        class="mb-6 elevation-2"
        :class="isMobile ? 'pa-1' : 'pa-4'"
      >
        <div ref="chartWrapEl" class="clubs-chart-wrap">
          <svg
            class="clubs-chart"
            :class="{ 'clubs-chart--mobile': isMobile }"
            :viewBox="`0 0 ${chartLayout.width} ${chartLayout.height}`"
            :style="isMobile
              ? { aspectRatio: `${chartLayout.width} / ${chartLayout.height}` }
              : undefined"
            preserveAspectRatio="xMidYMid meet"
            role="img"
            :aria-label="$t('ranking_clubs.sparkline_label')"
          >
            <line
              v-for="(tick, idx) in chartLayout.yTicks"
              :key="`y-${idx}`"
              :x1="chartLayout.padding.left"
              :y1="tick.y"
              :x2="chartLayout.plotRight"
              :y2="tick.y"
              class="clubs-chart__grid"
            />

            <g v-for="series in chartLayout.series" :key="series.club_id">
              <polyline
                :points="series.linePoints"
                :stroke="series.color"
                class="clubs-chart__line"
                fill="none"
              />
              <circle
                v-for="(point, pointIdx) in series.points"
                :key="`${series.club_id}-${pointIdx}`"
                :cx="point.x"
                :cy="point.y"
                :r="isMobile ? 5 : 5.5"
                :fill="series.color"
                class="clubs-chart__point"
                @pointerenter="showPointTip($event, series, point)"
                @pointerleave="hidePointTip"
              />
              <text
                :x="series.labelX"
                :y="series.labelY"
                class="clubs-chart__club-label"
                :fill="series.color"
                dominant-baseline="middle"
                :style="{ fontSize: `${chartLayout.fontSize}px` }"
              >
                {{ series.club_shortcut }}
              </text>
            </g>

            <text
              v-for="(tick, idx) in chartLayout.yTicks"
              :key="`ylabel-${idx}`"
              :x="chartLayout.padding.left - 8"
              :y="tick.y"
              class="clubs-chart__axis-label"
              text-anchor="end"
              dominant-baseline="middle"
              :style="{ fontSize: `${chartLayout.fontSize}px` }"
            >
              {{ tick.value }}
            </text>

            <text
              v-for="(race, idx) in globalRaces"
              :key="`xlabel-${idx}`"
              :x="chartLayout.xPositions[idx + 1]"
              :y="chartLayout.raceLabelY"
              class="clubs-chart__axis-label"
              :class="{ 'clubs-chart__axis-label--vertical': isMobile }"
              :text-anchor="isMobile ? 'end' : 'middle'"
              :transform="isMobile ? `rotate(-90, ${chartLayout.xPositions[idx + 1]}, ${chartLayout.raceLabelY})` : undefined"
              :style="{ fontSize: `${chartLayout.fontSize}px` }"
            >
              {{ truncateChartLabel(race.race_name, chartLayout.raceLabelMaxChars) }}
            </text>
          </svg>

          <v-tooltip
            :model-value="pointTip.open"
            :text="pointTip.text"
            location="top"
            :open-on-hover="false"
            :open-on-focus="false"
          >
            <template #activator="{ props: tipProps }">
              <span
                class="clubs-chart__tip-anchor"
                v-bind="tipProps"
                :style="pointTip.style"
              />
            </template>
          </v-tooltip>
        </div>
      </v-card>

      <v-data-table
        :headers="headers"
        :items="items"
        :sort-by="sortRanking"
        class="elevation-2"
        hide-default-footer
        :no-data-text="$t('ranking_clubs.no_data')"
        density="comfortable"
        :items-per-page="-1"
        striped="even"
        hover
      >
        <template v-slot:headers="{ columns, isSorted, getSortIcon, toggleSort }">
          <tr>
            <th
              v-for="col in columns"
              :key="col.value"
              :class="col.value.startsWith('race_') && !col.event_closed ? 'text-grey' : ''"
            >
              <div class="header-container">
                <div v-if="col.value.startsWith('race_')" class="vertical-header" @click="toggleSort(col)">
                  <v-tooltip location="bottom center" origin="auto">
                    <template v-slot:activator="{ props }">
                      <div v-bind="props">{{ col.title }}</div>
                      <div v-bind="props" class="race-date" @click="toggleSort(col)">{{ $formatDate(col.race_date) }}</div>
                      <v-icon
                        v-if="isSorted(col)"
                        :icon="getSortIcon(col)"
                        color="medium-emphasis"
                      />
                      <v-icon
                        v-else
                        :icon="getSortIcon(col)"
                        color="grey-lighten-1"
                      />
                    </template>
                    {{ col.event_name }}<br>
                    {{ col.title }}<br>
                    {{ $formatDate(col.race_date) }}
                  </v-tooltip>
                </div>
                <div v-else @click="toggleSort(col)">
                  {{ col.title }}
                  <v-icon
                    v-if="isSorted(col)"
                    :icon="getSortIcon(col)"
                    color="medium-emphasis"
                  />
                  <v-icon
                    v-else
                    :icon="getSortIcon(col)"
                    color="grey-lighten-1"
                  />
                </div>
              </div>
            </th>
          </tr>
        </template>

        <template v-slot:[`item.place`]="{ value }">
          <strong>{{ value }}</strong>
        </template>

        <template v-slot:[`item.club_name`]="{ item }">
          <router-link
            :to="`/oddil/${item.club_shortcut}`"
            class="club-name-link"
            :title="item.club_name"
          >
            {{ item.club_name }}
          </router-link>
        </template>

        <template v-slot:[`item.club_shortcut`]="{ value }">
          {{ value }}
        </template>

        <template v-slot:[`item.total_points`]="{ value }">
          <strong class="highlighted">{{ value }}</strong>
        </template>

        <template
          v-for="(race, idx) in globalRaces"
          :key="`race-${idx}`"
          v-slot:[`item.race_${idx}`]="{ value }"
        >
          <div :class="{ 'text-grey': !race.event_closed }">
            <span v-if="value" class="highlighted">{{ value }}</span>
          </div>
        </template>

        <template
          v-for="cat in rawCategories"
          :key="`cat-${cat}`"
          v-slot:[`item.category_${cat}`]="{ value }"
        >
          <span v-if="value" class="highlighted">{{ value }}</span>
        </template>
      </v-data-table>
      </template>
    </template>
  </v-container>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';
import apiClient from '/api.js';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import SelectYear from '@/components/shared/SelectYear.vue';
import { useSnackbarStore } from '@/stores/snackbarStore';

const display = useDisplay();
const isMobile = computed(() => display.smAndDown.value);
const chartWrapWidth = ref(1000);
let chartResizeObserver = null;

const loading = ref(true);
const exporting = ref(false);
const viewMode = ref('races');
const ranking = ref({ races: [], categories: [], clubs: [], drop_count: 0 });

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const snackbar = useSnackbarStore();
const thisYear = new Date().getFullYear();

const sortRanking = [{ key: 'total_points', order: 'desc' }];

const parseYear = (v) => {
  const y = Number(v);
  return Number.isFinite(y) ? y : thisYear;
};

const selectedYear = ref(parseYear(route.params.year));

const scoredRaceIndexes = computed(() => {
  const races = ranking.value.races || [];
  const clubs = ranking.value.clubs || [];
  return races
    .map((_, idx) => idx)
    .filter((idx) => clubs.some((club) => Number(club.race_points?.[idx]) > 0));
});

const globalRaces = computed(() => {
  const races = ranking.value.races || [];
  return scoredRaceIndexes.value.map((idx) => races[idx]);
});

const rawCategories = computed(() => ranking.value.categories || []);

const categoryColumns = computed(() =>
  rawCategories.value.map((cat) => (cat.startsWith('W') ? `D${cat.slice(1)}` : cat))
);

const headers = computed(() => {
  const fixed = [
    { title: 'Poř.', value: 'place', align: 'center', width: '30px' },
    { title: 'Klub', value: 'club_name', minWidth: '150px', sortable: true },
    { title: 'Zkratka', value: 'club_shortcut', sortable: true, align: 'center', width: '30px' },
    { title: 'Body', value: 'total_points', sortable: true, align: 'center' },
  ];

  if (viewMode.value === 'categories') {
    return [
      ...fixed,
      ...rawCategories.value.map((cat, index) => ({
        title: categoryColumns.value[index],
        value: `category_${cat}`,
        sortable: true,
        align: 'center',
      })),
    ];
  }

  return [
    ...fixed,
    ...globalRaces.value.map((race, index) => ({
      title: race.race_name,
      value: `race_${index}`,
      event_closed: race.event_closed,
      sortable: true,
      align: 'center',
      race_date: race.race_date,
      event_name: race.event_name,
      sort: (a, b) => (Number(a) || 0) - (Number(b) || 0),
    })),
  ];
});

const items = computed(() =>
  (ranking.value.clubs || []).map((club) => {
    const row = {
      place: club.place,
      club_id: club.club_id,
      club_name: club.club_name,
      club_shortcut: club.club_shortcut,
      total_points: club.total_points,
    };

    if (viewMode.value === 'categories') {
      rawCategories.value.forEach((cat) => {
        const val = club.category_points?.[cat];
        row[`category_${cat}`] = val > 0 ? val : null;
      });
    } else {
      scoredRaceIndexes.value.forEach((origIdx, displayIdx) => {
        const points = club.race_points?.[origIdx];
        row[`race_${displayIdx}`] = points > 0 ? points : null;
      });
    }

    return row;
  })
);

const CHART_COLORS = [
  '#1976D2', '#388E3C', '#F57C00', '#7B1FA2', '#C62828',
  '#00838F', '#5D4037', '#455A64', '#6A1B9A', '#EF6C00',
];

const toCumulative = (values) => {
  let sum = 0;
  return values.map((value) => {
    sum += Number(value) || 0;
    return sum;
  });
};

const buildYTicks = (maxY) => {
  if (maxY <= 0) return [0];
  const step = maxY <= 100 ? 25 : maxY <= 500 ? 100 : maxY <= 1000 ? 200 : 500;
  const ticks = [0];
  for (let v = step; v < maxY; v += step) {
    ticks.push(v);
  }
  if (ticks[ticks.length - 1] !== maxY) {
    ticks.push(Math.round(maxY));
  }
  return ticks;
};

const adjustLabelPositions = (labels, minGap, minY, maxY) => {
  if (!labels.length) return labels;

  const sorted = [...labels].sort((a, b) => a.labelY - b.labelY);
  sorted[0].labelY = Math.max(minY, Math.min(maxY, sorted[0].labelY));

  for (let i = 1; i < sorted.length; i += 1) {
    sorted[i].labelY = Math.max(sorted[i - 1].labelY + minGap, sorted[i].labelY);
  }

  for (let i = sorted.length - 2; i >= 0; i -= 1) {
    if (sorted[i + 1].labelY - sorted[i].labelY < minGap) {
      sorted[i].labelY = sorted[i + 1].labelY - minGap;
    }
  }

  sorted.forEach((item) => {
    item.labelY = Math.max(minY, Math.min(maxY, item.labelY));
  });

  return labels;
};

const truncateChartLabel = (text, maxLen) => {
  const value = String(text || '');
  if (value.length <= maxLen) return value;
  if (maxLen <= 3) return '...';
  return `${value.slice(0, maxLen - 3)}...`;
};

const chartLayout = computed(() => {
  const clubs = ranking.value.clubs || [];
  const races = globalRaces.value;
  const raceIndexes = scoredRaceIndexes.value;
  const raceCount = races.length;
  const mobile = isMobile.value;
  const width = Math.max(Math.round(chartWrapWidth.value) || 1000, 320);
  const fontSize = mobile ? 11 : 14;
  const maxShortcutLen = Math.max(
    ...clubs.map((club) => (club.club_shortcut || club.club_name || '').length),
    3,
  );
  const paddingTop = mobile ? 16 : 24;
  const paddingLeft = mobile ? 44 : 56;
  const paddingRight = 20 + maxShortcutLen * (mobile ? 7 : 10);
  const paddingBottom = mobile ? 108 : 56;
  const innerH = mobile ? 200 : 400 - paddingTop - paddingBottom;
  const height = mobile ? paddingTop + innerH + paddingBottom : 400;
  const padding = {
    top: paddingTop,
    right: paddingRight,
    bottom: paddingBottom,
    left: paddingLeft,
  };
  const plotRight = width - padding.right;
  const innerW = plotRight - padding.left;
  const plotBottom = padding.top + innerH;
  const raceLabelY = mobile ? plotBottom + 6 : height - padding.bottom + 24;
  const raceLabelMaxChars = mobile
    ? Math.max(4, Math.floor((padding.bottom - 12) / (fontSize * 0.62)))
    : Math.max(4, Math.floor((innerW / Math.max(raceCount, 1)) / (fontSize * 0.55)));

  if (!raceCount || !clubs.length) {
    return {
      width,
      height,
      padding,
      plotRight,
      plotBottom,
      fontSize,
      raceLabelY,
      raceLabelMaxChars,
      series: [],
      yTicks: [],
      xPositions: [],
    };
  }

  const filteredPointsByClub = clubs.map((club) =>
    raceIndexes.map((origIdx) => Number(club.race_points?.[origIdx]) || 0)
  );
  const cumulativeByClub = filteredPointsByClub.map((points) => toCumulative(points));
  const allValues = cumulativeByClub.flat();
  const maxY = Math.max(...allValues, 1);

  const xPositions = Array.from({ length: raceCount + 1 }, (_, idx) =>
    padding.left + (idx * innerW) / raceCount
  );

  const yFor = (value) => padding.top + innerH - (value / maxY) * innerH;

  const rawSeries = clubs.map((club, index) => {
    const cumulative = cumulativeByClub[index];
    const points = [
      { x: xPositions[0], y: yFor(0), val: 0, raceIndex: null },
      ...cumulative.map((val, raceIdx) => ({
        x: xPositions[raceIdx + 1],
        y: yFor(val),
        val,
        raceIndex: raceIdx,
      })),
    ];
    const lastPoint = points[points.length - 1];

    return {
      club_id: club.club_id,
      club_name: club.club_name,
      club_shortcut: club.club_shortcut || club.club_name,
      color: CHART_COLORS[index % CHART_COLORS.length],
      linePoints: points.map((p) => `${p.x},${p.y}`).join(' '),
      points,
      labelX: plotRight + 8,
      labelY: lastPoint?.y ?? padding.top + innerH / 2,
    };
  });

  const series = adjustLabelPositions(
    rawSeries,
    16,
    padding.top + 8,
    padding.top + innerH - 8,
  );

  const yTicks = buildYTicks(maxY).map((value) => ({
    value,
    y: yFor(value),
  }));

  return {
    width,
    height,
    padding,
    plotRight,
    plotBottom,
    fontSize,
    raceLabelY,
    raceLabelMaxChars,
    series,
    yTicks,
    xPositions,
  };
});

const chartPointTooltip = (series, point) => {
  const raceLabel = point.raceIndex === null
    ? '0'
    : (globalRaces.value[point.raceIndex]?.race_name || '');
  return `${series.club_shortcut || series.club_name} – ${raceLabel}: ${point.val}`;
};

const chartWrapEl = ref(null);
const pointTip = ref({
  open: false,
  text: '',
  style: { left: '0px', top: '0px' },
});

const syncChartWrapWidth = () => {
  const el = chartWrapEl.value;
  if (!el) return;
  const next = Math.round(el.clientWidth);
  if (next > 0) chartWrapWidth.value = next;
};

onMounted(() => {
  syncChartWrapWidth();
  if (typeof ResizeObserver === 'undefined') return;
  chartResizeObserver = new ResizeObserver(() => syncChartWrapWidth());
  if (chartWrapEl.value) chartResizeObserver.observe(chartWrapEl.value);
});

onBeforeUnmount(() => {
  chartResizeObserver?.disconnect();
  chartResizeObserver = null;
});

watch(chartWrapEl, (el, prev) => {
  if (prev && chartResizeObserver) chartResizeObserver.unobserve(prev);
  if (el && chartResizeObserver) {
    chartResizeObserver.observe(el);
    syncChartWrapWidth();
  }
}, { flush: 'post' });

const showPointTip = (event, series, point) => {
  const wrap = chartWrapEl.value;
  if (!wrap) return;

  const wrapRect = wrap.getBoundingClientRect();
  const circleRect = event.currentTarget.getBoundingClientRect();

  pointTip.value = {
    open: true,
    text: chartPointTooltip(series, point),
    style: {
      left: `${circleRect.left + circleRect.width / 2 - wrapRect.left}px`,
      top: `${circleRect.top + circleRect.height / 2 - wrapRect.top}px`,
    },
  };
};

const hidePointTip = () => {
  pointTip.value = { ...pointTip.value, open: false };
};

const fetchRanking = async (year) => {
  loading.value = true;

  try {
    const response = await apiClient.get(`/ranking/clubs/${year}/`);
    ranking.value = response.data || { races: [], categories: [], clubs: [], drop_count: 0 };
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404) {
      ranking.value = { races: [], categories: [], clubs: [], drop_count: 0 };
    }
    console.error(error);
  } finally {
    loading.value = false;
  }
};

const downloadExport = async () => {
  exporting.value = true;
  try {
    const response = await apiClient.get(
      `/ranking/clubs/${selectedYear.value}/export/`,
      { responseType: 'blob' }
    );
    const disposition = response.headers['content-disposition'] || '';
    const match = disposition.match(/filename="?([^"]+)"?/i);
    const filename = match?.[1] || `zebricek_oddilu_ROB_${selectedYear.value}.pdf`;
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error(error);
    snackbar.error(t('ranking_clubs.export_failed'));
  } finally {
    exporting.value = false;
  }
};

watch(
  () => route.params.year,
  (ny) => {
    const y = parseYear(ny);
    if (y !== selectedYear.value) selectedYear.value = y;
    fetchRanking(y);
  },
  { immediate: true }
);

watch(selectedYear, (y) => {
  if (parseYear(route.params.year) !== y) {
    router.replace({ name: 'RankingClubs', params: { year: String(y) } });
  }
});
</script>

<style scoped>
.clubs-chart-wrap {
  position: relative;
  width: 100%;
}

.clubs-chart {
  width: 100%;
  height: 400px;
  display: block;
}

.clubs-chart--mobile {
  height: auto;
}

.clubs-chart__point {
  cursor: pointer;
}

.clubs-chart__tip-anchor {
  position: absolute;
  width: 1px;
  height: 1px;
  pointer-events: none;
  transform: translate(-50%, -50%);
}

.year-select {
  width: 100%;
  max-width: 160px;
}

.clubs-chart__grid {
  stroke: rgba(var(--v-border-color), var(--v-border-opacity));
  stroke-width: 1;
}

.clubs-chart__line {
  stroke-width: 2.5;
}

.clubs-chart--mobile .clubs-chart__line {
  stroke-width: 3.5;
}

.clubs-chart--mobile .clubs-chart__grid {
  stroke-width: 1.5;
}

.clubs-chart__axis-label,
.clubs-chart__club-label {
  font-family: inherit;
}

.clubs-chart__axis-label {
  fill: rgba(var(--v-theme-on-surface), 0.75);
}

.clubs-chart__club-label {
  font-weight: 600;
}

.vertical-header {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  white-space: nowrap;
  padding: 5px;
  font-size: 13px;
  margin-left: auto;
  margin-right: auto;
  min-height: 90px;
  vertical-align: bottom;
}

.race-date {
  color: gray;
}

.header-container {
  white-space: nowrap;
}

.highlighted {
  font-size: 15px;
}

.text-grey {
  color: grey !important;
}

.club-name-link {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

:deep() .v-table .v-table__wrapper > table > thead > tr > th {
  border-bottom: none !important;
  padding: 0px 10px;
}

:deep() .v-table .v-table__wrapper > table > thead > tr > th:nth-child(4) {
  background-color: rgb(var(--v-theme-ranking-points)) !important;
}

:deep() .v-table .v-table__wrapper > table > tbody > tr > td:not(:last-child) {
  border-right: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
}

:deep() .v-table .v-table__wrapper > table > thead > tr > th {
  vertical-align: bottom;
}

:deep() .v-table .v-table__wrapper > table > thead > tr > th:nth-child(1),
:deep() .v-table .v-table__wrapper > table > thead > tr > th:nth-child(3),
:deep() .v-table .v-table__wrapper > table > thead > tr > th:nth-child(4) {
  text-align: center !important;
}

:deep() .v-table .v-table__wrapper > table > thead > tr > th:nth-child(2) {
  text-align: left !important;
}

:deep() .v-table > .v-table__wrapper > table > tbody > tr > td {
  padding: 0px 10px;
}

:deep(.v-data-table__td:nth-child(4)) {
  background-color: rgb(var(--v-theme-ranking-points)) !important;
}
</style>
